import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { useShallowEqualSelector } from '@/hooks';
import { setPage } from '@/store/billing';
import { fetchPayments } from '@/store/billing/thunks';
import { showError } from '@/utils';

import Table from './BillingTable/Table';
import EmptySection from './EmptySection';

const DEFAULT_PAGE = 1;

function History() {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const pageURL = parseInt(searchParams.get('page'), 10) || DEFAULT_PAGE;
	const { t } = useTranslation();
	const { completed, isEmpty, isLoading, page, rows, pagination } =
		useShallowEqualSelector(({ billing }) => ({
			completed: billing.fetchPayments.completed,
			isEmpty: billing.isEmpty,
			isLoading: billing.fetchPayments.loading,
			page: billing.page,
			rows: billing.rows,
			pagination: billing.pagination
		}));

	const handlePageChange = newPage => {
		dispatch(setPage(newPage));
	};

	useEffect(() => {
		if (pageURL) {
			dispatch(setPage(pageURL));
		}
	}, []);

	useEffect(() => {
		setSearchParams(page === DEFAULT_PAGE ? {} : { page });
		dispatch(fetchPayments()).unwrap().then().catch(showError);
	}, [page]);

	return (
		<div className="relative flex flex-col gap-2 md:w-full md:gap-4">
			<h2 className="text-xs font-semibold leading-5">
				{t(`billing.subscription.history`)}
			</h2>
			{isLoading && (
				<Spinner
					className="absolute inset-x-0 top-[96px] z-10 mx-auto text-black md:top-[104px]"
					size={40}
				/>
			)}
			{isEmpty && <EmptySection className="py-16" />}
			{!isEmpty && (
				<div className="flex flex-col gap-3 md:gap-4">
					<Table history={rows} ready={completed} />
					{pagination?.total_pages > 1 && (
						<Pagination
							className="mt-2"
							onChange={handlePageChange}
							page={page}
							totalPages={pagination?.total_pages}
						/>
					)}
				</div>
			)}
		</div>
	);
}

export default History;
